import React, { useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import moment from 'moment';

import {
  addExpense,
  attachFiles,
  changeSalary,
  cleanPayout,
  deferLine,
  deleteOwnExpense,
  excludeLine,
  getPayout,
  getRequisites,
  getSalary,
  payeeRef,
  removeAttachment,
  restoreLine,
  stopRecurringLine,
} from '@/store/actions/payout';
import { selectPayout, selectRequisites, selectSalary } from '@/store/selectors/payout';
import { PayoutLine } from '@/store/reducers/payout';

import useLanguage from '@/components/common/hooks/useLanguage';
import useToggle from '@/components/common/hooks/useToggle';
import BackIcon from '@/components/common/icons/BackIcon';
import Loader from '@/components/common/Loader';
import AddExpense from '@/components/payout/AddExpense';
import ExpenseLine from '@/components/payout/ExpenseLine';
import Requisites from '@/components/payout/Requisites';
import SalaryQuickActions from '@/components/payout/SalaryQuickActions';
import { formatMoney, formatMoneyWithUah, formatSigned } from '@/utils/money';

interface Props extends RouteComponentProps<{ userId: string }> {}

const asMonth = (period: string) => moment(period, 'YYYY-MM').format('MMM YYYY');

/**
 * Admin-only, and the single place a person's pay lives: what they earn, what
 * that is about to add up to, and what it has been.
 *
 * The two histories at the bottom are deliberately not merged: `history` is the
 * salary that was agreed, `payoutHistory` is what the accounting sheet says was
 * actually paid each month, bonuses and all.
 */
const UserSalary: React.FC<Props> = ({ match, history }) => {
  const { userId } = match.params;
  const dispatch = useDispatch();
  const salary = useSelector(selectSalary);
  const nextPayout = useSelector(selectPayout);
  const requisites = useSelector(selectRequisites);
  const [{ payout }] = useLanguage();
  const [isAdding, toggleIsAdding] = useToggle(false);

  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [showPaid, setShowPaid] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // A layout effect, so the stored copies render before the first paint -- see
  // store/responses.ts. On a switch to another person the cleanup wipes this
  // one's figures in the same commit, so they are never painted under the new
  // name.
  useLayoutEffect(() => {
    dispatch(getSalary(+userId));
    dispatch(getPayout({ userId: +userId }));
    dispatch(getRequisites({ userId: +userId }));

    return () => {
      dispatch(cleanPayout());
    };
  }, [userId]);

  const target = { userId: +userId };

  const submit = (value: number, note: string) => {
    if (!(value > 0)) return;
    dispatch(changeSalary(+userId, value, note || undefined));
    setAmount('');
    setComment('');
  };

  if (!salary) return <Loader />;

  const parsed = Number(amount.replace(',', '.'));

  return (
    <>
      <Header>
        <HeaderWrap>
          <TopButton onClick={history.goBack}>
            <BackIcon />
          </TopButton>
          <HeaderText>{payout.salary_and_payout}</HeaderText>
        </HeaderWrap>
      </Header>

      <Main className="scrollbar">
        <Current>
          <CurrentLabel>{payout.current_salary}</CurrentLabel>
          <CurrentValue>
            {salary.current === undefined
              ? payout.not_set
              : formatMoney(salary.current)}
          </CurrentValue>
          {nextPayout && (
            <Breakdown>
              <BreakdownRow>
                <span>
                  {`${payout.next_payout} · ${moment(nextPayout.period, 'YYYY-MM').format('MMM YYYY')}`}
                </span>
                <strong>
                  {formatMoneyWithUah(
                    nextPayout.total !== undefined
                      ? nextPayout.total
                      : nextPayout.expensesTotal,
                    nextPayout.expensesTotalUah,
                  )}
                </strong>
              </BreakdownRow>
              <BreakdownRow>
                <span>{payout.expenses}</span>
                <span>
                  {formatMoneyWithUah(nextPayout.expensesTotal, nextPayout.expensesTotalUah)}
                </span>
              </BreakdownRow>
            </Breakdown>
          )}
        </Current>

        <SalaryQuickActions current={salary.current} onPick={submit} />

        <Label>{payout.new_amount}</Label>
        <Input
          value={amount}
          inputMode="decimal"
          placeholder="0"
          onChange={(e) => setAmount(e.target.value)}
        />
        <Input
          style={{ marginTop: '8px' }}
          value={comment}
          placeholder={payout.change_salary}
          onChange={(e) => setComment(e.target.value)}
        />
        <Primary disabled={!(parsed > 0)} onClick={() => submit(parsed, comment)}>
          {payout.save}
        </Primary>

        <SectionHead>
          <Label style={{ margin: 0 }}>{payout.expenses}</Label>
          <AddLink onClick={() => toggleIsAdding(true)}>
            {payout.add_expense}
          </AddLink>
        </SectionHead>
        {nextPayout && nextPayout.lines.length === 0 && (
          <Empty>{payout.no_expenses}</Empty>
        )}
        {nextPayout && nextPayout.lines.map((line) => (
          <ExpenseLine
            key={line.ref}
            line={line}
            isAdmin
            onDefer={(l: PayoutLine) => dispatch(deferLine(l, nextPayout.period, target))}
            onExclude={(l: PayoutLine) => dispatch(excludeLine(l, nextPayout.period, target))}
            onRestore={(l: PayoutLine) => dispatch(restoreLine(l, target))}
            onStopRecurring={(l: PayoutLine) => (
              dispatch(stopRecurringLine(l, nextPayout.period, target))
            )}
            onDelete={(l: PayoutLine) => (
              l.expenseId ? dispatch(deleteOwnExpense(l.expenseId, target)) : undefined
            )}
            onAttach={(l: PayoutLine, files: File[]) => (
              dispatch(attachFiles(l, files, nextPayout.period, target))
            )}
            onRemoveAttachment={(a) => dispatch(removeAttachment(a, target))}
          />
        ))}

        {requisites && requisites.payee === payeeRef(target) && (
          <Requisites payee={target} requisite={requisites.requisite} />
        )}

        {salary.history.length === 0 ? (
          <>
            <Label>{payout.salary_history}</Label>
            <Empty>{payout.not_set}</Empty>
          </>
        ) : (
          <Expander onClick={() => setShowHistory(!showHistory)}>
            {`${payout.salary_history} (${salary.history.length})`}
            <Chevron isOpen={showHistory}>›</Chevron>
          </Expander>
        )}
        {showHistory && salary.history.map((record) => (
          <Record key={record._id}>
            <RecordLeft>
              <RecordAmount>{formatMoney(record.amount)}</RecordAmount>
              {Boolean(record.comment) && (
                <RecordComment>{record.comment}</RecordComment>
              )}
            </RecordLeft>
            <RecordRight>
              <RecordPeriod>{asMonth(record.effectiveFrom)}</RecordPeriod>
              {record.change !== undefined && record.change !== 0 && (
                <Change positive={record.change > 0}>
                  {formatSigned(record.change)}
                </Change>
              )}
            </RecordRight>
          </Record>
        ))}

        {salary.payoutHistory.length > 0 && (
          <>
            <Expander onClick={() => setShowPaid(!showPaid)}>
              {`${payout.paid_history} (${salary.payoutHistory.length})`}
              <Chevron isOpen={showPaid}>›</Chevron>
            </Expander>
            {showPaid && (
              <>
                <Note>{payout.imported_note}</Note>
                {salary.payoutHistory.map((paid) => (
                  <Record key={paid._id}>
                    <RecordLeft>
                      <RecordAmount>{formatMoney(paid.amount)}</RecordAmount>
                      {Boolean(paid.note) && <RecordComment>{paid.note}</RecordComment>}
                    </RecordLeft>
                    <RecordPeriod>{asMonth(paid.period)}</RecordPeriod>
                  </Record>
                ))}
              </>
            )}
          </>
        )}
      </Main>

      {isAdding && (
        <AddExpense
          onAdd={(body, files) => dispatch(addExpense(body, target, files))}
          onClose={() => toggleIsAdding(false)}
        />
      )}
    </>
  );
};

const Header = styled.header`
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.17);
  background-color: #ffffff;
  height: 72px;
  position: sticky;
  z-index: 100;
  width: 100%;
  top: 0;
`;

const HeaderWrap = styled.div`
  width: 100%;
  height: 72px;
  display: flex;
  align-items: center;
  padding: 12px 16px;
`;

const HeaderText = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #21272e;
  margin-left: 24px;
`;

const TopButton = styled.div`
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Main = styled.div`
  padding: 16px 16px 80px;
  background: #ffffff;
  height: 100%;
  overflow-y: auto;
  flex: 1;
`;

const Current = styled.div`
  border-radius: 6px;
  border: solid 1px #dae1e8;
  padding: 16px;
  text-align: center;
`;

const CurrentLabel = styled.div`
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.3px;
  color: #909599;
`;

const CurrentValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #21272e;
  margin-top: 6px;
`;

const Breakdown = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: solid 1px #eceff2;
  text-align: left;
`;

const BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #787c80;
  padding: 3px 0;
`;

const SectionHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0 4px;
`;

const AddLink = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #fb7e14;
  cursor: pointer;
`;

const Expander = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.3px;
  color: #909599;
  margin: 24px 0 8px;
  cursor: pointer;
  user-select: none;
`;

const Chevron = styled.span<{ isOpen: boolean }>`
  font-size: 16px;
  line-height: 1;
  transition: transform 160ms ease;
  transform: rotate(${({ isOpen }) => (isOpen ? '90deg' : '0deg')});
`;

const Label = styled.div`
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.3px;
  color: #909599;
  margin: 24px 0 8px;
`;

const Note = styled.div`
  font-size: 12px;
  color: #909599;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  border-radius: 6px;
  border: solid 1px #dae1e8;
  padding: 0 12px;
  font-size: 14px;
  color: #21272e;
  outline: none;

  :focus {
    border-color: #fb7e14;
  }
`;

const Primary = styled.button`
  width: 100%;
  height: 48px;
  margin-top: 12px;
  border: none;
  border-radius: 6px;
  background: #fb7e14;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  :disabled {
    background: #dae1e8;
    cursor: default;
  }
`;

const Record = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: solid 1px #eceff2;
`;

const RecordLeft = styled.div`
  min-width: 0;
`;

const RecordRight = styled.div`
  text-align: right;
  margin-left: 12px;
  white-space: nowrap;
`;

const RecordAmount = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #21272e;
`;

const RecordComment = styled.div`
  font-size: 12px;
  color: #909599;
  margin-top: 2px;
  word-break: break-word;
`;

const RecordPeriod = styled.div`
  font-size: 12px;
  color: #909599;
  white-space: nowrap;
`;

const Change = styled.div<{ positive: boolean }>`
  font-size: 12px;
  font-weight: 700;
  margin-top: 2px;
  color: ${({ positive }) => (positive ? '#188038' : '#d9534f')};
`;

const Empty = styled.div`
  font-size: 14px;
  color: #909599;
  padding: 12px 0;
`;

export default UserSalary;
